import { useState, useMemo, useRef, type ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { bulkImportShippers } from '../../api/endpoints/admin';
import type { VendorImportResponse, ShipperImportRecord } from '../../api/types';
import { useMutationHook } from '../../hooks/useMutationHook';
import { DataTable } from '../ui/DataTable';
import {
  parseSpreadsheetFile,
  extractSheetData,
  autoMapShipperHeaders,
  SHIPPER_FIELDS,
  type ShipperFieldKey,
  type ParsedSpreadsheet,
} from '../../utils/spreadsheetParser';
import {
  mapAndValidateShipperRows,
  type DefaultShipperImportValues,
  type ShipperValidationSummary,
} from '../../utils/importValidation';
import styles from './VendorImportModal.module.css';
import { DialogShell } from '../ui/DialogShell';
import { useDialogDismiss } from '../ui/dialogDismiss';
import formStyles from './FormModal.module.css';
import { DialogHelp } from '../../help/DialogHelp';

interface Props {
  onClose: () => void;
}

const MAX_SIZE_BYTES = 15 * 1024 * 1024; // 15MB

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

type Step = 'upload' | 'mapping' | 'preview' | 'importing' | 'results';

export function ShipperImportModal({ onClose }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [spreadsheet, setSpreadsheet] = useState<ParsedSpreadsheet | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [mapping, setMapping] = useState<Record<ShipperFieldKey, string>>({
    phoneNumber: '',
    companyName: '',
    fullName: '',
    email: '',
    gstin: '',
    billingAddress: '',
    secondaryPhoneNumber: '',
    whatsappNumber: '',
  });

  const [defaults, setDefaults] = useState<DefaultShipperImportValues>({
    companyName: '',
    fullName: '',
    billingAddress: '',
  });

  const [overrideExisting, setOverrideExisting] = useState<boolean>(false);
  const [filterErrorsOnly, setFilterErrorsOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<VendorImportResponse | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compute validation summary on demand
  const validationSummary: ShipperValidationSummary = useMemo(() => {
    if (!spreadsheet) {
      return {
        totalRows: 0,
        validRows: 0,
        invalidRows: 0,
        hasErrors: false,
        duplicatePhoneCount: 0,
        missingRequiredCount: 0,
        mappedRows: [],
      };
    }
    return mapAndValidateShipperRows(spreadsheet.rawRows, mapping, defaults);
  }, [spreadsheet, mapping, defaults]);

  const importMut = useMutationHook(
    async () => {
      const recordsToImport: ShipperImportRecord[] = validationSummary.mappedRows.map((r) => ({
        phoneNumber: r.phoneNumber,
        companyName: r.companyName,
        fullName: r.fullName,
        email: r.email,
        gstin: r.gstin,
        billingAddress: r.billingAddress,
        secondaryPhoneNumber: r.secondaryPhoneNumber,
        whatsappNumber: r.whatsappNumber,
      }));

      return bulkImportShippers(recordsToImport, (done, total) => {
        setProgress({ done, total });
      }, overrideExisting);
    },
    {
      successMessage: (data) =>
        data.failures.length === 0
          ? `Imported ${plural(data.imported.length, 'shipper')}.`
          : data.imported.length === 0
            ? `${plural(data.failures.length, 'row')} failed validation — nothing was imported.`
            : `Imported ${plural(data.imported.length, 'shipper')}, then stopped at a failing row.`,
      invalidateKeys: [
        ['admin', 'users'],
        ['admin', 'dashboard'],
      ],
      onSuccess: (data) => {
        setResult(data);
        setStep('results');
      },
      onError: (err) => {
        setError(err.message || 'Import failed. Please try again.');
        setStep('preview');
      },
    },
  );

  const handleFileChosen = async (chosenFile: File) => {
    if (chosenFile.size > MAX_SIZE_BYTES) {
      setError('File must be smaller than 15 MB.');
      return;
    }
    setError(null);
    setFile(chosenFile);

    try {
      const parsed = await parseSpreadsheetFile(chosenFile);
      setSpreadsheet(parsed);
      setSelectedSheet(parsed.activeSheet);

      // Heuristic auto-mapping
      const autoMapped = autoMapShipperHeaders(parsed.headers);
      setMapping(autoMapped);
    } catch (e) {
      setError((e as Error).message || 'Failed to read spreadsheet.');
    }
  };

  const handleSheetChange = (sheetName: string) => {
    if (!spreadsheet) return;
    setSelectedSheet(sheetName);
    const { headers, rawRows } = extractSheetData(spreadsheet.workbook, sheetName);
    setSpreadsheet({
      ...spreadsheet,
      activeSheet: sheetName,
      headers,
      rawRows,
      totalRows: rawRows.length,
    });
    setMapping(autoMapShipperHeaders(headers));
  };

  const handleMappingChange = (key: ShipperFieldKey, value: string) => {
    setMapping((prev) => ({ ...prev, [key]: value }));
  };

  const startImport = () => {
    if (validationSummary.hasErrors) {
      setError('Cannot proceed with import while there are validation errors.');
      return;
    }
    setError(null);
    setStep('importing');
    importMut.mutate(undefined);
  };

  const resetAll = () => {
    setFile(null);
    setSpreadsheet(null);
    setSelectedSheet('');
    setResult(null);
    setError(null);
    setProgress(null);
    setStep('upload');
  };

  const displayedRows = filterErrorsOnly
    ? validationSummary.mappedRows.filter((r) => !r.isValid)
    : validationSummary.mappedRows;

  // A picked file is work in progress until the results step; nothing closes mid-import.
  const dismiss = useDialogDismiss({
    dirty: file !== null && step !== 'results',
    busy: importMut.isLoading || step === 'importing',
    onClose,
  });

  return (
    <DialogShell dismiss={dismiss} label={t('domain.shipperImportModal.title')} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      <div className={formStyles.titleRow}>
        <h2 className={styles.title}>{t('domain.shipperImportModal.title')}</h2>
        <DialogHelp context="bulk-import" title={t('domain.shipperImportModal.title')} />
      </div>
      <p className={styles.subtitle}>
        {t('domain.shipperImportModal.subtitle')}
      </p>

      {/* Stepper Header */}
      <div className={styles.stepper}>
        <div
          className={`${styles.stepItem} ${step === 'upload' ? styles.stepItemActive : spreadsheet ? styles.stepItemDone : ''}`}
        >
          <span className={styles.stepNumber}>1</span>
          <span>{t('domain.shipperImportModal.stepUpload')}</span>
        </div>
        <div className={styles.stepDivider} />
        <div
          className={`${styles.stepItem} ${step === 'mapping' ? styles.stepItemActive : step === 'preview' || step === 'results' ? styles.stepItemDone : ''}`}
        >
          <span className={styles.stepNumber}>2</span>
          <span>{t('domain.shipperImportModal.stepMapping')}</span>
        </div>
        <div className={styles.stepDivider} />
        <div
          className={`${styles.stepItem} ${step === 'preview' ? styles.stepItemActive : step === 'results' ? styles.stepItemDone : ''}`}
        >
          <span className={styles.stepNumber}>3</span>
          <span>{t('domain.shipperImportModal.stepPreview')}</span>
        </div>
        <div className={styles.stepDivider} />
        <div
          className={`${styles.stepItem} ${step === 'results' ? styles.stepItemActive : ''}`}
        >
          <span className={styles.stepNumber}>4</span>
          <span>{t('domain.shipperImportModal.stepResults')}</span>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* ─── STEP 1: Upload & Sheet Selection ─── */}
      {step === 'upload' && (
        <div>
          {!file ? (
            <div
              className={styles.dropzone}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const dropped = e.dataTransfer.files?.[0];
                if (dropped) handleFileChosen(dropped);
              }}
            >
              <div className={styles.dropzoneIcon}>📊</div>
              <div className={styles.dropzoneText}>
                {t('domain.shipperImportModal.dropzoneText')}
              </div>
              <div className={styles.dropzoneSub}>
                {t('domain.shipperImportModal.dropzoneSub')}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: 'none' }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const selected = e.target.files?.[0];
                  if (selected) handleFileChosen(selected);
                }}
              />
            </div>
          ) : (
            <div>
              <div className={styles.fileInfoCard}>
                <div>
                  <div className={styles.fileName}>{file.name}</div>
                  <div className={styles.fileMeta}>
                    {t('domain.shipperImportModal.fileSize', { size: (file.size / 1024).toFixed(1) })}{' '}
                    {spreadsheet ? `${spreadsheet.totalRows} data rows detected` : 'Parsing...'}
                  </div>
                </div>
                <button type="button" className={styles.changeFileBtn} onClick={resetAll}>
                  {t('domain.shipperImportModal.changeFile')}
                </button>
              </div>

              {spreadsheet && spreadsheet.sheetNames.length > 1 && (
                <div className={styles.sheetSelector}>
                  <label className={styles.sheetLabel}>{t('domain.shipperImportModal.selectSheet')}</label>
                  <select
                    className={styles.selectInput}
                    value={selectedSheet}
                    onChange={(e) => handleSheetChange(e.target.value)}
                  >
                    {spreadsheet.sheetNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {spreadsheet && (
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>{t('domain.shipperImportModal.detectedColumns', { n: spreadsheet.headers.length })}</div>
                  <div className={styles.sectionDesc}>
                    {spreadsheet.headers.join(' • ')}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={dismiss.requestClose}>
              {t('common.actions.cancel')}
            </button>
            <button
              type="button"
              className={styles.submit}
              disabled={!spreadsheet || spreadsheet.totalRows === 0}
              onClick={() => setStep('mapping')}
            >
              {t('domain.shipperImportModal.nextMapping')}
            </button>
          </div>
        </div>
      )}

      {/* ─── STEP 2: Column Mapping & Default Values ─── */}
      {step === 'mapping' && (
        <div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('domain.shipperImportModal.columnMapping')}</div>
            <p className={styles.sectionDesc}>
              {t('domain.shipperImportModal.columnMappingDesc')}
            </p>

            <div className={styles.mappingList}>
              {SHIPPER_FIELDS.map((field) => {
                const currentMapped = mapping[field.key];
                return (
                  <div key={field.key} className={styles.mappingRow}>
                    <div className={styles.fieldInfo}>
                      <div className={styles.fieldLabelRow}>
                        <span>{field.label}</span>
                        {field.required ? (
                          <span className={styles.badgeRequired}>{t('domain.shipperImportModal.required')}</span>
                        ) : (
                          <span className={styles.badgeOptional}>{t('domain.shipperImportModal.optional')}</span>
                        )}
                      </div>
                      <div className={styles.fieldDesc}>{field.description}</div>
                      {currentMapped && (
                        <div className={styles.badgeMatched}>{t('domain.shipperImportModal.matchedTo', { column: currentMapped })}</div>
                      )}
                    </div>

                    <select
                      className={styles.selectInput}
                      value={currentMapped}
                      onChange={(e) => handleMappingChange(field.key, e.target.value)}
                    >
                      <option value="">{t('domain.shipperImportModal.doNotMap')}</option>
                      {spreadsheet?.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Default / Fallback Values Section */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('domain.shipperImportModal.defaultsTitle')}</div>
            <p className={styles.sectionDesc}>
              {t('domain.shipperImportModal.defaultsDesc')}
            </p>

            <div className={styles.defaultsGrid}>
              <div className={styles.defaultField}>
                <label className={styles.defaultLabel}>{t('domain.shipperImportModal.defaultCompanyName')}</label>
                <input
                  className={styles.defaultInput}
                  placeholder={t('domain.shipperImportModal.defaultCompanyNamePlaceholder')}
                  value={defaults.companyName ?? ''}
                  onChange={(e) => setDefaults({ ...defaults, companyName: e.target.value })}
                />
              </div>

              <div className={styles.defaultField}>
                <label className={styles.defaultLabel}>{t('domain.shipperImportModal.defaultContactPerson')}</label>
                <input
                  className={styles.defaultInput}
                  placeholder={t('domain.shipperImportModal.defaultContactPersonPlaceholder')}
                  value={defaults.fullName ?? ''}
                  onChange={(e) => setDefaults({ ...defaults, fullName: e.target.value })}
                />
              </div>

              <div className={styles.defaultField}>
                <p className={styles.sectionDesc}>{t('domain.shipperImportModal.kycPendingHint')}</p>
              </div>

              <div className={styles.defaultField} style={{ gridColumn: 'span 2' }}>
                <label className={styles.defaultLabel}>{t('domain.shipperImportModal.defaultBillingAddress')}</label>
                <input
                  className={styles.defaultInput}
                  placeholder={t('domain.shipperImportModal.defaultBillingAddressPlaceholder')}
                  value={defaults.billingAddress ?? ''}
                  onChange={(e) => setDefaults({ ...defaults, billingAddress: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Handling Already Registered Shippers */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('domain.shipperImportModal.existingTitle')}</div>
            <p className={styles.sectionDesc}>
              {t('domain.shipperImportModal.existingDesc')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '14px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="radio"
                  name="overrideStrategy"
                  checked={!overrideExisting}
                  onChange={() => setOverrideExisting(false)}
                  style={{ marginTop: '2px' }}
                />
                <div>
                  <strong>{t('domain.shipperImportModal.noOverride')}</strong>
                  <div style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '2px' }}>
                    {t('domain.shipperImportModal.noOverrideDesc')}
                  </div>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="radio"
                  name="overrideStrategy"
                  checked={overrideExisting}
                  onChange={() => setOverrideExisting(true)}
                  style={{ marginTop: '2px' }}
                />
                <div>
                  <strong>{t('domain.shipperImportModal.override')}</strong>
                  <div style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '2px' }}>
                    {t('domain.shipperImportModal.overrideDesc')}
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={() => setStep('upload')}>
              {t('domain.shipperImportModal.backToUpload')}
            </button>
            <button type="button" className={styles.submit} onClick={() => setStep('preview')}>
              {t('domain.shipperImportModal.nextPreview')}
            </button>
          </div>
        </div>
      )}

      {/* ─── STEP 3: Preview & Pre-Validation ─── */}
      {step === 'preview' && (
        <div>
          <div className={styles.statsBar}>
            <div className={styles.statPill}>
              <Trans i18nKey="domain.shipperImportModal.totalRecords" values={{ n: validationSummary.totalRows }} components={{ strong: <strong /> }} />
            </div>
            <div className={`${styles.statPill} ${styles.statPillValid}`}>
              <Trans i18nKey="domain.shipperImportModal.readyToImport" values={{ n: validationSummary.validRows }} components={{ strong: <strong /> }} />
            </div>
            {validationSummary.invalidRows > 0 && (
              <div className={`${styles.statPill} ${styles.statPillError}`}>
                <Trans i18nKey="domain.shipperImportModal.errorsWarnings" values={{ n: validationSummary.invalidRows }} components={{ strong: <strong /> }} />
              </div>
            )}

            {validationSummary.invalidRows > 0 && (
              <label className={styles.filterToggle}>
                <input
                  type="checkbox"
                  checked={filterErrorsOnly}
                  onChange={(e) => setFilterErrorsOnly(e.target.checked)}
                />
                {t('domain.shipperImportModal.showErrorsOnly', { n: validationSummary.invalidRows })}
              </label>
            )}
          </div>

          {validationSummary.hasErrors && (
            <div className={styles.error}>
              {t('domain.shipperImportModal.hasErrors', { n: validationSummary.invalidRows })}
            </div>
          )}

          <div className={styles.previewTableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '45px' }}>#</th>
                  <th>{t('domain.shipperImportModal.colCompanyName')}</th>
                  <th>{t('domain.shipperImportModal.colPhone')}</th>
                  <th>{t('domain.shipperImportModal.colAltPhone')}</th>
                  <th>{t('domain.shipperImportModal.colWhatsapp')}</th>
                  <th>{t('domain.shipperImportModal.colContactPerson')}</th>
                  <th>{t('domain.shipperImportModal.colEmail')}</th>
                  <th>{t('domain.shipperImportModal.colGstin')}</th>
                  <th>{t('domain.shipperImportModal.colBillingAddress')}</th>
                  <th>{t('domain.shipperImportModal.colValidation')}</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row) => (
                  <tr key={row.rowNumber} className={row.isValid ? '' : styles.tableRowError}>
                    <td style={{ color: 'var(--muted)' }}>{row.rowNumber}</td>
                    <td>
                      <strong>{row.companyName || '—'}</strong>
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                      {row.phoneNumber || '—'}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                      {row.secondaryPhoneNumber || '—'}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                      {row.whatsappNumber || '—'}
                    </td>
                    <td>{row.fullName || '—'}</td>
                    <td>{row.email || '—'}</td>
                    <td>{row.gstin || '—'}</td>
                    <td>{row.billingAddress || '—'}</td>
                    <td>
                      {row.isValid ? (
                        <span style={{ color: 'var(--status-green-text-2)', fontWeight: 600 }}>
                          {t('domain.shipperImportModal.valid')}
                        </span>
                      ) : (
                        <div>
                          {row.errors.map((err, i) => (
                            <div key={i} className={styles.rowErrorPill}>
                              {err}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={() => setStep('mapping')}>
              {t('domain.shipperImportModal.backToMapping')}
            </button>
            <button
              type="button"
              className={styles.submit}
              disabled={validationSummary.hasErrors || importMut.isLoading}
              onClick={startImport}
            >
              {importMut.isLoading
                ? 'Importing...'
                : `Import ${validationSummary.validRows} Shippers`}
            </button>
          </div>
        </div>
      )}

      {/* ─── STEP 4: Importing / In-Progress ─── */}
      {step === 'importing' && (
        <div className={styles.progressContainer}>
          <div className={styles.progressBarTrack}>
            <div
              className={styles.progressBarFill}
              style={{
                width: `${progress ? Math.round((progress.done / Math.max(progress.total, 1)) * 100) : 0}%`,
              }}
            />
          </div>
          <div className={styles.progressText}>
            {t('domain.shipperImportModal.progress', { done: progress?.done ?? 0, total: progress?.total ?? validationSummary.validRows })}
          </div>
          <div className={styles.progressSub}>
            {t('domain.shipperImportModal.progressSub')}
          </div>
        </div>
      )}

      {/* ─── STEP 5: Results ─── */}
      {step === 'results' && result && (
        <div>
          {result.failures.length === 0 ? (
            <p className={`${styles.summary} ${styles.summarySuccess}`}>
              {t('domain.shipperImportModal.resultSuccess', { shippers: plural(result.imported.length, 'shipper') })}
            </p>
          ) : result.imported.length === 0 ? (
            <p className={`${styles.summary} ${styles.summaryFailure}`}>
              {t('domain.shipperImportModal.resultNoneImported', { rows: plural(result.failures.length, 'row') })}
            </p>
          ) : (
            <p className={`${styles.summary} ${styles.summaryFailure}`}>
              {t('domain.shipperImportModal.resultPartial', { shippers: plural(result.imported.length, 'shipper'), row: result.failures[0].row })}
            </p>
          )}

          {result.failures.length > 0 && (
            <div className={styles.resultsWrapper}>
              <DataTable
                keyFn={(f) => f.row}
                rows={result.failures}
                columns={[
                  { key: 'row', header: 'Row', width: '64px', render: (f) => f.row },
                  { key: 'company', header: 'Company', render: (f) => f.companyName ?? '—' },
                  { key: 'reason', header: 'Reason', render: (f) => f.reason },
                ]}
              />
            </div>
          )}

          {result.imported.length > 0 && (
            <div className={styles.resultsWrapper}>
              <DataTable
                keyFn={(s) => s.userId}
                rows={result.imported}
                columns={[
                  { key: 'id', header: 'User ID', width: '90px', render: (s) => `#${s.userId}` },
                  { key: 'company', header: 'Company', render: (s) => s.companyName },
                  { key: 'phone', header: 'Phone', width: '160px', render: (s) => s.phoneNumber },
                ]}
              />
            </div>
          )}

          <div className={styles.actions}>
            {result.failures.length > 0 && (
              <button type="button" className={styles.cancel} onClick={resetAll}>
                {t('domain.shipperImportModal.uploadDifferent')}
              </button>
            )}
            <button type="button" className={styles.submit} onClick={onClose}>
              {t('domain.shipperImportModal.done')}
            </button>
          </div>
        </div>
      )}
    </DialogShell>
  );
}
